/**
 * Inquiry Inbox Page
 * Main page for managing customer inquiries
 * Story 6.5
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { SellerDashboardLayout } from '@/modules/marketplace/components/SellerDashboardLayout';
import { InquiryTable } from '@/modules/marketplace/components/InquiryTable';
import { InquiryDetailModal } from '@/modules/marketplace/components/InquiryDetailModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, RotateCcw, LayoutDashboard, Package, MessageSquare, PlusCircle } from 'lucide-react';
import { useInquiries, useUpdateInquiryStatus } from '@/modules/marketplace/hooks/useInquiries';
import { useInquiryFilters, STATUS_OPTIONS } from '@/modules/marketplace/hooks/useInquiryFilters';
import { InquiryRow, InquiryStatus } from '@/modules/marketplace/types/inquiries.types';

/**
 * Props interface for InquiryInboxPage component
 */
export interface InquiryInboxPageProps {
  /** When true, renders without SellerDashboardLayout wrapper (for embedding in other layouts) */
  disableLayout?: boolean;
}

export function InquiryInboxPage({ disableLayout = false }: InquiryInboxPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryRow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  const filters = useInquiryFilters();
  const [searchInput, setSearchInput] = useState<string>(filters.filters.search ?? '');
  const { inquiries, pagination, isLoading, isError, error, refetch } = useInquiries(
    filters.filters
  );
  const { updateStatus, isPending: isUpdating } = useUpdateInquiryStatus();

  // Debounce search input → applied filter. Whitespace-only input applies no filter,
  // so pressing space doesn't wipe the list.
  useEffect(() => {
    const trimmed = searchInput.trim();
    const nextSearch = trimmed.length > 0 ? trimmed : undefined;
    if (nextSearch === filters.filters.search) return;

    const timer = setTimeout(() => {
      filters.setSearch(nextSearch);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Keep local input in sync when filters are cleared externally (Clear All, navigation reset)
  useEffect(() => {
    const applied = filters.filters.search ?? '';
    if (applied !== searchInput.trim()) {
      setSearchInput(applied);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.filters.search]);

  // Apply productId filter from URL query params on mount
  useEffect(() => {
    const productIdFromUrl = searchParams.get('productId');
    if (productIdFromUrl) {
      // Set the productId filter if it's in the URL
      filters.setProductId(productIdFromUrl);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear filters when navigating away from inquiry pages to prevent state bleeding
  // across module navigation (e.g., going from Product Actions → Inquiry to another module)
  useEffect(() => {
    const isInquiryPath = location.pathname.includes('/inquiries') ||
                          location.pathname.includes('/marketplace/seller/inquiries') ||
                          location.pathname === '/sales/inquiries';

    // If we're NOT on an inquiry page, clear the inquiry filters
    if (!isInquiryPath) {
      filters.clearFilters();
      // Clear URL search params
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleRowClick = (inquiry: InquiryRow) => {
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);
  };

  const handleStatusUpdate = async (status: InquiryStatus, estimatedQuote?: number) => {
    if (!selectedInquiry) return;

    try {
      await updateStatus(selectedInquiry.id, status, undefined, estimatedQuote);
      // Refetch to get updated data
      await refetch();
      // Update selected inquiry with new status
      setSelectedInquiry((prev) => (prev ? { ...prev, status } : null));
    } catch (error) {
      console.error('Failed to update inquiry status:', error);
      // Error is already handled in the modal component with toast
      // Re-throw to let the modal component handle it
      throw error;
    }
  };

  const breadcrumbs = [
    { label: 'Dashboard', path: '/marketplace/seller/dashboard' },
    { label: 'Inquiries', path: '/marketplace/seller/inquiries' },
  ];

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/marketplace/seller/dashboard' },
    { id: 'products', label: 'My Products', icon: Package, path: '/marketplace/seller/products' },
    { id: 'inquiries', label: 'Inquiries', icon: MessageSquare, path: '/marketplace/seller/inquiries' },
    { id: 'add-product', label: 'Add Product', icon: PlusCircle, path: '/marketplace/seller/products/add' },
  ];

  const hasActiveFilters = useMemo(() => {
    return (
      filters.filters.status !== 'ALL' ||
      filters.filters.productId ||
      filters.filters.search
    );
  }, [filters.filters]);

  // Get product name from the first inquiry if filtering by product
  const productFilterName = useMemo(() => {
    if (filters.filters.productId && inquiries.length > 0) {
      return inquiries[0].product;
    }
    return null;
  }, [filters.filters.productId, inquiries]);

  // Page content (with or without layout wrapper)
  const pageContent = (
    <>
      {/* Product Filter Indicator */}
      {filters.filters.productId && productFilterName && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Filtering by Product
              </p>
              <p className="text-xs text-blue-700">
                Showing inquiries for: <span className="font-semibold">{productFilterName}</span>
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              filters.setProductId(undefined);
              // Also clear the productId from URL
              setSearchParams({});
            }}
            className="gap-1 text-blue-700 hover:text-blue-900 hover:bg-blue-100"
          >
            <RotateCcw className="h-4 w-4" />
            Clear Product Filter
          </Button>
        </div>
      )}

      {/* Filters Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={filters.clearFilters}
                className="gap-1"
              >
                <RotateCcw className="h-4 w-4" />
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Status Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.filters.status}
                onValueChange={(value) => filters.setStatus(value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Customer Name/Email</label>
              <Input
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Page Size */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Results per page</label>
              <Select
                value={filters.filters.pageSize.toString()}
                onValueChange={(value) => filters.setPageSize(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 50, 100].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sorting */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Sort by</label>
              <Select
                value={filters.filters.sortBy || 'createdAt'}
                onValueChange={(value) =>
                  filters.setSorting(
                    value as 'createdAt' | 'status' | 'customerName',
                    filters.filters.sortOrder || 'desc'
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt">Date (Newest)</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="customerName">Customer Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div
        className="mb-4 text-sm text-gray-600"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span>
          Showing {inquiries.length} of {pagination.totalItems} inquiries
          {hasActiveFilters && ' (filtered)'}
        </span>
      </div>

      {/* Table */}
      <InquiryTable
        inquiries={inquiries}
        isLoading={isLoading}
        isError={isError}
        error={error}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        onRowClick={handleRowClick}
        onNextPage={() => filters.setPage(pagination.page + 1)}
        onPrevPage={() => filters.setPage(pagination.page - 1)}
        onRetry={refetch}
      />

      {/* Detail Modal */}
      <InquiryDetailModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        inquiry={selectedInquiry}
        isUpdating={isUpdating}
        onStatusUpdate={handleStatusUpdate}
        onClose={() => setSelectedInquiry(null)}
      />
    </>
  );

  // Conditionally wrap content with SellerDashboardLayout
  if (disableLayout) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Inquiries</h2>
          <p className="text-gray-600 text-sm mt-1">Manage customer inquiries and respond to questions</p>
        </div>
        {pageContent}
      </div>
    );
  }

  return (
    <SellerDashboardLayout
      navigationItems={navigationItems}
      breadcrumbs={breadcrumbs}
      pageTitle="Inquiries"
    >
      {pageContent}
    </SellerDashboardLayout>
  );
}

export default InquiryInboxPage;
